import { Link } from "@tanstack/react-router";

import { Button } from "@/components/ui";

import { MarketingShell } from "./MarketingShell";
import { useSession } from "./useSession";

import "./landing.css";

/** The landing page, per `docs/ui/marketing/landing.md`. No free tier, no price, streaming only. */
export function Landing() {
  const { data: account } = useSession();
  const signedIn = account != null;
  return (
    <MarketingShell
      nav={
        <>
          <a href="#tracker">Eye tracker</a>
          <a href="#chat">Chat play</a>
          <a href="#how">How it works</a>
          <Link to="/pricing">Pricing</Link>
        </>
      }
    >
      <section className="mk-hero">
        <div>
          <h1 className="mk-h1">Your audience plays on your screen.</h1>
          <p className="mk-lede">
            Viewers take a hex, block your path and vote on what you do next, drawn over your
            game in OBS. You look at a hex to claim it.
          </p>
          <div className="mk-cta">
            {signedIn ? (
              <Link to="/console">
                <Button size="lg">Open the console</Button>
              </Link>
            ) : (
              <Link to="/pricing">
                <Button size="lg">See pricing</Button>
              </Link>
            )}
            <Link to="/activities">
              <Button size="lg" variant="ghost">
                Browse activities
              </Button>
            </Link>
          </div>
        </div>

        <div>
          {/* Decorative. Every claim it makes is in the text beside it. */}
          <div className="mk-stage" role="presentation">
            <span className="mk-stage-label">broadcast canvas</span>
            <span className="mk-hex" style={{ left: "18%", top: "24%" }} />
            <span className="mk-hex" style={{ left: "32%", top: "40%" }} />
            <span className="mk-hex mk-hex-off" style={{ left: "46%", top: "24%" }} />
            <span className="mk-hex mk-hex-off" style={{ left: "46%", top: "56%" }} />
            <span className="mk-hex" style={{ left: "60%", top: "40%" }} />
            <span className="mk-bar">
              <i />
            </span>
          </div>
          <p className="mk-caption">a hex arena over a live capture</p>
        </div>
      </section>

      <section className="mk-section" id="tracker">
        <div className="mk-split">
          <div>
            <h2 className="mk-h2">You need a Tobii Eye Tracker 5</h2>
            <p className="mk-body">
              It is a separate purchase and it is not optional for the games you play with your
              eyes. Buying the subscription without one leaves you with activities you cannot
              drive.
            </p>
          </div>
          <div>
            <h2 className="mk-h2" id="chat">
              Your chat plays without one
            </h2>
            <p className="mk-body">
              Voting, buzzers, submissions and prize wheels run from a phone or from chat. No
              tracker on either side, yours or theirs.
            </p>
          </div>
        </div>
      </section>

      <section className="mk-section">
        <h2 className="mk-h2">Where you looked never leaves your computer</h2>
        <div className="mk-note">
          <p className="mk-body">
            The tracker drives the game on your machine. Gaze coordinates are not uploaded, not
            stored and not readable through any API. What reaches the cloud is which hex you
            claimed, the same as if you had clicked it.
          </p>
        </div>
      </section>

      <section className="mk-section" id="how">
        <h2 className="mk-h2">How it works</h2>
        <div className="mk-steps">
          <div className="mk-step">
            <div className="mk-step-n">01</div>
            <h3>Paste one address into OBS</h3>
            <p>A browser source you paste once. It carries no password and never changes.</p>
          </div>
          <div className="mk-step">
            <div className="mk-step-n">02</div>
            <h3>Add an activity</h3>
            <p>Pick from the catalog. Each one brings its own canvases and settings with it.</p>
          </div>
          <div className="mk-step">
            <div className="mk-step-n">03</div>
            <h3>Share the join address</h3>
            <p>Viewers open it on a phone. No account, no install, no download.</p>
          </div>
        </div>
      </section>

      <section className="mk-section">
        <h2 className="mk-h2">Pricing is by how many people play at once</h2>
        <p className="mk-body">
          Plans are tiered by concurrent participants. Streaming is a paid product: there is no
          free tier.
        </p>
        <div className="mk-cta">
          <Link to="/pricing">
            <Button>See pricing</Button>
          </Link>
          <Link to="/publish">
            <Button variant="ghost">Build an activity</Button>
          </Link>
        </div>
      </section>
    </MarketingShell>
  );
}
